import React from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../../context/LanguageContext';

const WelcomePopup = ({
  showWelcomePopup,
  handleCloseWelcomePopup,
  handlePopupBackdropClick,
  handleStartProductTour,
  theme,
  currentThemeName
}) => {
  const { t } = useLanguage();

  if (!showWelcomePopup) return null;

  const isDark = currentThemeName === 'dark';
  const isBeige = currentThemeName === 'beige';

  const overlay = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-popup-title"
      onClick={handlePopupBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 24,
        boxSizing: 'border-box',
        background: isDark
          ? 'rgba(15, 23, 42, 0.75)'
          : isBeige
          ? 'rgba(139, 69, 19, 0.2)'
          : 'rgba(15, 23, 42, 0.4)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        animation: 'welcomePopupSlideIn 0.4s ease-out'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.modalBg ?? (isDark ? '#1e293b' : isBeige ? '#faf5eb' : '#ffffff'),
          borderRadius: 24,
          padding: '40px 36px',
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
          border: isDark
            ? '1px solid rgba(59, 130, 246, 0.2)'
            : isBeige
            ? '1px solid rgba(139, 69, 19, 0.15)'
            : `1px solid ${theme.primaryBorder || '#e2e8f0'}`,
          boxShadow: isDark
            ? '0 25px 50px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)'
            : '0 25px 50px -12px rgba(0,0,0,0.15)',
          position: 'relative'
        }}
      >
        {/* Close */}
        <button
          type="button"
          onClick={handleCloseWelcomePopup}
          aria-label="Close welcome"
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            border: 'none',
            fontSize: 22,
            cursor: 'pointer',
            color: theme.secondaryText || (isDark ? '#94a3b8' : '#64748b'),
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            transition: 'background 0.2s, color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
            e.currentTarget.style.color = theme.primaryText || (isDark ? '#e2e8f0' : '#1e293b');
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
            e.currentTarget.style.color = theme.secondaryText || (isDark ? '#94a3b8' : '#64748b');
          }}
        >
          ×
        </button>

        {/* Icon */}
        <div
          style={{
            width: 72,
            height: 72,
            background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
            borderRadius: '50%',
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 32,
            color: 'white',
            boxShadow: '0 8px 32px rgba(59, 130, 246, 0.35)',
            animation: 'welcomeIconPulse 2s ease-in-out infinite'
          }}
        >
          👋
        </div>

        {/* Title */}
        <h1
          id="welcome-popup-title"
          style={{
            fontSize: '1.75rem',
            fontWeight: 700,
            marginBottom: 12,
            background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
          }}
        >
          {t('fullChat.welcome.title')}
        </h1>

        {/* Message */}
        <p
          style={{
            fontSize: '1rem',
            lineHeight: 1.6,
            color: theme.secondaryText || (isDark ? '#cbd5e1' : '#475569'),
            marginBottom: 28,
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            padding: '0 8px'
          }}
        >
          {t('fullChat.welcome.message')}
        </p>

        {/* Buttons */}
        <div
          className="welcome-popup-buttons"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <button
            type="button"
            onClick={handleCloseWelcomePopup}
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: 'white',
              border: 'none',
              borderRadius: 14,
              padding: '14px 28px',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              boxShadow: '0 4px 14px rgba(59, 130, 246, 0.35)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(59, 130, 246, 0.35)';
            }}
          >
            {t('fullChat.welcome.getStarted')}
          </button>

          <button
            type="button"
            onClick={() => {
              handleCloseWelcomePopup();
              setTimeout(() => handleStartProductTour(), 300);
            }}
            style={{
              background: 'transparent',
              color: theme.primary || '#3b82f6',
              border: `2px solid ${theme.primary || '#3b82f6'}`,
              borderRadius: 14,
              padding: '14px 28px',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = theme.primary || '#3b82f6';
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = theme.primary || '#3b82f6';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {t('fullChat.welcome.takeTour')}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default WelcomePopup;
